import { GupshupSubscriptionsListResponse } from '@bandido/shared/types';
import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';
import { Add, Delete } from '@mui/icons-material';
import { Box, Card, CardActions, CardContent, Button, Typography, Skeleton, useTheme, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { GupshupCreateSubscription } from '../GupShupCreateSubscription';
import { useState } from 'react';

const fetchSubscriptions = async (agencyId: number): Promise<GupshupSubscriptionsListResponse> => {
  const { data } = await client.get(`/whatsapp/gupshup/subscriptions/${agencyId}`);
  return data;
};

const deleteSubscription = async (id: string, agencyId: number) => {
  await client.delete(`/whatsapp/gupshup/subscriptions/${id}/${agencyId}`);
};

export const GupshupSubscriptions = ({ agencyId }: { agencyId: number }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { data, error, isLoading } = useQuery({
    queryKey: [QueryKeys.gupshupSubscriptions],
    queryFn: () => fetchSubscriptions(agencyId),
  });
  const { t } = useTranslation();

  const { mutate: handleDelete, isLoading: isDeleting } = useMutation({
    mutationFn: (id: string) => deleteSubscription(id, agencyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.gupshupSubscriptions] });
    },
  });

  if (error) return <pre>{error.toString()}</pre>;

  return (
    <Box>
      <Box display="flex" justifyContent="flex-start" alignItems="center" gap={2}>
        <Typography variant="subtitle1">Subscriptions</Typography>
        <IconButton onClick={() => setOpen(true)} color="primary">
          <Add />
        </IconButton>
      </Box>
      <Box display="flex" flexWrap="wrap" gap={2} padding={2}>
        {isLoading
          ? Array.from({ length: 3 }).map((_, index) => (
              <Box key={index} flex="1 1 calc(33.333% - 16px)" minWidth="300px">
                <Card>
                  <CardContent>
                    <Skeleton variant="text" width="60%" height={40} />
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="80%" />
                  </CardContent>
                  <CardActions>
                    <Skeleton variant="rectangular" width={100} height={36} />
                  </CardActions>
                </Card>
              </Box>
            ))
          : data?.subscriptions?.map((subscription) => (
              <Box
                key={subscription.id}
                p={2}
                borderRadius={1}
                maxWidth="400px"
                border={`1px solid ${theme.palette.text.disabled}`}
              >
                <Typography variant="h6" gutterBottom>
                  {subscription.tag}
                </Typography>
                <Box marginBottom={2}>
                  <Typography variant="body1" color="text.secondary">
                    URL:
                  </Typography>
                  <Typography variant="subtitle2">{subscription.url}</Typography>
                </Box>
                <Box marginBottom={2}>
                  <Typography variant="body1" color="text.secondary">
                    App ID:
                  </Typography>
                  <Typography variant="subtitle2">{subscription.appId}</Typography>
                </Box>
                <Box marginBottom={2}>
                  <Typography variant="body1" color="text.secondary">
                    {t('common.mode')}s:
                  </Typography>
                  <Typography variant="subtitle2">{subscription.modes.join(', ')}</Typography>
                </Box>
                <Box marginBottom={2}>
                  <Typography variant="body1" color="text.secondary">
                    {t('common.active')}:
                  </Typography>
                  <Typography variant="subtitle2" color={subscription.active ? 'success.main' : 'error.main'}>
                    {subscription.active ? t('common.yes') : t('common.no')}
                  </Typography>
                </Box>

                <Box display="flex" flexDirection="row-reverse">
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => handleDelete(subscription.id)}
                    disabled={isDeleting}
                  >
                    {t('common.delete')}
                  </Button>
                </Box>
              </Box>
            ))}
      </Box>
      <GupshupCreateSubscription open={open} onClose={() => setOpen(false)} agencyId={agencyId} />
    </Box>
  );
};
